<template>
    <div>
        <BigButton
            task="addToCart"
            class="bg-order-buttons font-extrabold text-white"
            :disabled="disabled"
            @click="handleClick"
            :loading="isAddingToCart"
            data-test-id="add-to-cart-button"
        />

        <ProductOptionsModal v-model:open="modalOpen" />

        <AddToCartErrorModal
            v-model:open="cartError"
            :description="$t('pages.product_detail_page.product_info.product_button.add_to_cart.error')"
            @retry="handleClick"
        />

        <ActionStatus kind="addToCart" :state="!suppressLoadingOverlay && isAddingToCart ? 'loading' : null" />
    </div>
</template>

<script setup lang="ts">
import BigButton from '@/components/buttons/BigButton.vue';
import AddToCartErrorModal from '@/components/cart/partials/AddToCartErrorModal.vue';
import { useAddToCart } from '@/composables/cart/useAddToCart';
import ActionStatus from '@/components/status/ActionStatus.vue';
import ProductOptionsModal from '@/pages/product-page/partials/product-info/partials/modals/ProductOptionsModal.vue';
import { useProductOptionsStore } from '@/stores/product-options/ProductOptionsStore';
import { useProductStore } from '@/stores/products/ProductStore';
import { ref } from 'vue';

type ButtonProps = {
    disabled?: boolean;
    /**
     * The AI cart-bake flow drives this button through triggerClick() and shows its own popup card
     * for the whole span from the cart tap to the request settling — see AiUploadButton and
     * useAiCartBakeFlow. Without this, the moment this button's own click actually starts the
     * request, this StatusOverlay opened too, stacking a second loading popup on top of that card.
     */
    suppressLoadingOverlay?: boolean;
};

const props = withDefaults(defineProps<ButtonProps>(), {
    disabled: false,
    suppressLoadingOverlay: false,
});

const modalOpen = ref(false);

const productStore = useProductStore();
const productOptionsStore = useProductOptionsStore();
const { addToCart, isLoading: isAddingToCart, cartError } = useAddToCart();

defineExpose({ triggerClick: () => handleClick(), isAddingToCart, cartError });

function handleClick(): void {
    if (!productOptionsStore.productOptions || productOptionsStore.productOptions.length === 0) {
        void addToCart(productStore.configurableProduct.group_identifier, productStore.selectedProduct.product.sku, 1, []);

        return;
    }

    modalOpen.value = true;
}
</script>
